/**
 * Domain Entity: Post
 * Represents a social media post with business logic
 */
export class Post {
  private constructor(
    private readonly _id: string,
    private readonly _userId: string,
    private readonly _title: string,
    private readonly _content: string,
    private readonly _category: string,
    private readonly _tags: string[],
    private readonly _likes: number = 0,
    private readonly _views: number = 0,
    private readonly _comments: number = 0,
    private readonly _isPublished: boolean = false,
    private readonly _isFeatured: boolean = false,
    private readonly _createdAt: Date = new Date(),
    private readonly _updatedAt: Date = new Date(),
    private readonly _publishedAt?: Date
  ) {}

  static create(
    id: string,
    userId: string,
    title: string,
    content: string,
    category: string,
    tags: string[] = []
  ): Post {
    if (!id || !userId || !title || !content) {
      throw new Error('Post ID, User ID, title, and content are required');
    }

    if (title.trim().length === 0) {
      throw new Error('Post title cannot be empty');
    }

    if (content.trim().length === 0) {
      throw new Error('Post content cannot be empty');
    }

    if (title.length > 200) {
      throw new Error('Post title cannot exceed 200 characters');
    }

    if (content.length > 10000) {
      throw new Error('Post content cannot exceed 10000 characters');
    }

    return new Post(id, userId, title, content, category, tags);
  }

  static fromData(data: {
    id: string;
    user_id: string;
    title: string;
    content: string;
    category: string;
    tags: string[];
    likes?: number;
    views?: number;
    comments?: number;
    is_published?: boolean;
    is_featured?: boolean;
    created_at?: string;
    updated_at?: string;
    published_at?: string;
  }): Post {
    return new Post(
      data.id,
      data.user_id,
      data.title,
      data.content,
      data.category,
      data.tags,
      data.likes || 0,
      data.views || 0,
      data.comments || 0,
      data.is_published || false,
      data.is_featured || false,
      data.created_at ? new Date(data.created_at) : new Date(),
      data.updated_at ? new Date(data.updated_at) : new Date(),
      data.published_at ? new Date(data.published_at) : undefined
    );
  }

  get id(): string {
    return this._id;
  }

  get userId(): string {
    return this._userId;
  }

  get title(): string {
    return this._title;
  }

  get content(): string {
    return this._content;
  }

  get category(): string {
    return this._category;
  }

  get tags(): string[] {
    return [...this._tags];
  }

  get likes(): number {
    return this._likes;
  }

  get views(): number {
    return this._views;
  }

  get comments(): number {
    return this._comments;
  }

  get isPublished(): boolean {
    return this._isPublished;
  }

  get isFeatured(): boolean {
    return this._isFeatured;
  }

  get createdAt(): Date {
    return this._createdAt;
  }

  get updatedAt(): Date {
    return this._updatedAt;
  }

  get publishedAt(): Date | undefined {
    return this._publishedAt;
  }

  get totalEngagement(): number {
    return this._likes + this._views + this._comments;
  }

  get engagementRate(): number {
    if (this._views === 0) return 0;
    return (this._likes + this._comments) / this._views;
  }

  isOwner(userId: string): boolean {
    return this._userId === userId;
  }


  isDraft(): boolean {
    return !this._isPublished;
  }

  isRecent(days: number = 7): boolean {
    const now = new Date();
    const diffMs = now.getTime() - this._createdAt.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays <= days;
  }

  hasTag(tag: string): boolean {
    return this._tags.includes(tag);
  }

  addTag(tag: string): Post {
    if (!this._tags.includes(tag)) {
      return new Post(
        this._id,
        this._userId,
        this._title,
        this._content,
        this._category,
        [...this._tags, tag],
        this._likes,
        this._views,
        this._comments,
        this._isPublished,
        this._isFeatured,
        this._createdAt,
        new Date(),
        this._publishedAt
      );
    }
    return this;
  }

  removeTag(tag: string): Post {
    return new Post(
      this._id,
      this._userId,
      this._title,
      this._content,
      this._category,
      this._tags.filter(t => t !== tag),
      this._likes,
      this._views,
      this._comments,
      this._isPublished,
      this._isFeatured,
      this._createdAt,
      new Date(),
      this._publishedAt
    );
  }

  publish(): Post {
    return new Post(
      this._id,
      this._userId,
      this._title,
      this._content,
      this._category,
      this._tags,
      this._likes,
      this._views,
      this._comments,
      true,
      this._isFeatured,
      this._createdAt,
      new Date(),
      new Date()
    );
  }

  unpublish(): Post {
    return new Post(
      this._id,
      this._userId,
      this._title,
      this._content,
      this._category,
      this._tags,
      this._likes,
      this._views,
      this._comments,
      false,
      this._isFeatured,
      this._createdAt,
      new Date(),
      undefined
    );
  }

  toJSON() {
    return {
      id: this._id,
      user_id: this._userId,
      title: this._title,
      content: this._content,
      category: this._category,
      tags: this._tags,
      likes: this._likes,
      views: this._views,
      comments: this._comments,
      is_published: this._isPublished,
      is_featured: this._isFeatured,
      created_at: this._createdAt.toISOString(),
      updated_at: this._updatedAt.toISOString(),
      published_at: this._publishedAt?.toISOString()
    };
  }
}
